/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { COVERAGE_CODE, MEMBER_TYPE, QueryKeys, SPEND } from "@/constants";
import { queryClient } from "@/hooks/use-query-client";
import type { SpendSummary } from "@/model/spend-summary";
import { CheckCircle, XCircle } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { formatCurrency } from "@/lib/cost-util";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { lowerCaseComparison } from "@/lib/string-util";
import { formatDateToMMDDYYYY } from "@/lib/date-utils";
import { isCoverageActiveOrExtended } from "@/lib/utils";
import type { PlanCoverageInfo, Relation } from "@/model/demographics";

// Coverage-specific label overrides for the OOP/reimbursement section.
// Falls back to the default labels when a coverage code has no override.
type OopLabels = { long: string; short: string };

const DEFAULT_OOP_LABELS: OopLabels = { long: "Out of Pocket", short: "OOP" };

const OOP_LABELS_BY_COVERAGE_CODE: Partial<Record<COVERAGE_CODE, OopLabels>> = {
  [COVERAGE_CODE.DENTAL]: {
    long: "Reimbursement",
    short: "Reimbursement",
  },
};

// Coverage-specific overrides (e.g. dental's "Reimbursement") take priority.
// Otherwise, costSharingType (e.g. "Coinsurance") is the most specific label
// the API gives us for this spend cap, so prefer it over the generic "OOP"
// default whenever it names something other than a plain OOP max.
const getOopLabels = (
  coverageCode?: string,
  costSharingType?: string,
): OopLabels => {
  const coverageOverride =
    OOP_LABELS_BY_COVERAGE_CODE[coverageCode as COVERAGE_CODE];
  if (coverageOverride) return coverageOverride;

  if (costSharingType && !lowerCaseComparison(costSharingType, "oop")) {
    return { long: costSharingType, short: costSharingType };
  }
  return DEFAULT_OOP_LABELS;
};

// API-provided status labels (e.g. "OOP Max Met") hardcode "OOP" — swap in the
// coverage-specific short label while keeping the rest of the API text as-is.
export const withOopShortLabel = (
  label: string | undefined,
  coverageCode?: string,
) => label?.replace("OOP", getOopLabels(coverageCode).short) ?? "";

// API-provided labels (e.g. "Remaining Out-of-Pocket") spell it out in full —
// swap in the coverage-specific long label while keeping the rest of the API text as-is.
export const withOopLongLabel = (
  label: string | undefined,
  coverageCode?: string,
) => label?.replace(/out-of-pocket/i, getOopLabels(coverageCode).long) ?? "";

// Utility functions
const getProgressPercentage = (paid: number, max: number): number => {
  let percentage = max > 0 ? (paid / max) * 100 : 0;
  percentage = percentage > 100 ? 100 : percentage;
  percentage = percentage < 0 ? 0 : percentage;
  return Math.round(percentage);
};

// Sub-components
type AnimatedProgressProps = {
  value: number;
  color: string;
  backgroundColor: string;
  borderColor: string;
};

const AnimatedProgress: React.FC<AnimatedProgressProps> = ({
  value,
  color = "bg-blue-500",
  backgroundColor = "bg-blue-100",
  borderColor = "border-blue-200",
}) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(value);
    }, 100);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div
      className={`h-4 w-full overflow-hidden rounded-full ${backgroundColor} ${borderColor}`}
    >
      <div
        className={`h-full ${color} rounded-full transition-all duration-1000 ease-out`}
        style={{ width: `${animatedValue}%` }}
      />
    </div>
  );
};

const SpendSummaryHeader = ({
  memberId,
  type,
}: {
  memberId: string;
  type: string;
}) => {
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const members = queryClient.getQueryData([
    QueryKeys.MEMBERS_LOOKUP,
  ]) as Record<string, Relation>;
  const name = Object.keys(members).includes(memberId)
    ? `${members[memberId]?.firstName} ${members[memberId]?.lastName}`
    : memberId;
  const relation =
    type !== "Family"
      ? Object.keys(members).includes(memberId)
        ? members[memberId]?.relationship
        : ""
      : type;
  const mbrTermDate =
    type !== "Family"
      ? Object.keys(members).includes(memberId)
        ? members[memberId]?.planCoverageInfo?.find(
            (item) =>
              item?.claimCoverageCode === activeCoverage?.claimCoverageCode &&
              lowerCaseComparison(item?.status, "termed"),
          )?.mbrTermDate
        : ""
      : "";
  const formattedMbrTermDate = formatDateToMMDDYYYY(mbrTermDate);

  return (
    <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3">
      <div className="flex flex-row items-center justify-between gap-2 text-gray-800">
        <CardTitle
          as="h2"
          className="max-w-44 text-sm font-semibold leading-tight"
        >
          {name ?? ""}
        </CardTitle>

        {Boolean(formattedMbrTermDate) && (
          <span className="text-sm font-semibold">
            Terminated as of {formattedMbrTermDate}
          </span>
        )}

        <Badge
          className="flex w-fit items-center justify-center rounded-full bg-slate-200 text-xs capitalize text-slate-500"
          variant="secondary"
        >
          {/*Implemented as a part of https://zakipointhealth.atlassian.net/browse/CSRTB-253*/}
          {relation?.toLowerCase() === "employee"
            ? "Subscriber"
            : (relation?.toLowerCase() ?? "")}
        </Badge>
      </div>
    </CardHeader>
  );
};

const ProgressSection = ({
  title,
  paid,
  max,
  type = "oop",
}: {
  title: string;
  paid: number;
  max: number;
  type: "oop" | "deductible";
}) => {
  const percentage = getProgressPercentage(paid, max);
  const remaining = max - paid < 0 ? 0 : max - paid;
  const color = type === "oop" ? "bg-primary" : "bg-secondary";
  const borderColor =
    type === "oop" ? "border-primary-dark" : "border-secondary-dark";
  const textColor =
    type === "oop" ? "text-primary-extraDark" : "text-secondary-extraDark";
  const backgroundColor =
    type === "oop" ? "bg-primary-extraLight" : "bg-secondary-extraLight";

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${textColor}`}>{title}</span>
        <span className={`text-sm font-medium ${textColor}`}>
          {formatCurrency(paid)} / {formatCurrency(max)}
        </span>
      </div>
      <AnimatedProgress
        value={percentage}
        color={color}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
      />
      <div className="flex justify-between">
        <span className={`text-sm font-medium ${textColor}`}>
          {Math.round(percentage)}%
        </span>
        <span className={`text-sm font-medium ${textColor}`}>
          {formatCurrency(remaining)} remaining
        </span>
      </div>
    </div>
  );
};

type StatusBadgeProps = {
  isMetCondition: boolean;
  statusLabel: string;
  costSharingType: string;
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  isMetCondition,
  statusLabel,
  costSharingType = "oop",
}) => {
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const isOop =
    lowerCaseComparison(costSharingType, "oop") ||
    lowerCaseComparison(costSharingType, "coinsurance");
  const borderColor = isOop ? "border-primary-dark" : "border-secondary-dark";
  const textColor = isOop
    ? "text-primary-extraDark"
    : "text-secondary-extraDark";
  const backgroundColor = isOop
    ? "bg-primary-extraLight"
    : "bg-secondary-extraLight";

  const modStatusLabel = isOop
    ? withOopShortLabel(statusLabel, activeCoverage?.claimCoverageCode)
    : statusLabel;

  if (isMetCondition) {
    return (
      <Badge className="w-auto justify-center border-green-500 bg-green-200 text-xs font-normal text-green-800 transition-colors hover:bg-green-200">
        <CheckCircle className="mr-1 size-3" />
        {modStatusLabel}
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      className={`w-auto text-xs font-medium ${borderColor} ${textColor} ${backgroundColor} hover: transition-colors${backgroundColor} flex items-center justify-center`}
    >
      <XCircle className="mr-1 size-3" />
      {modStatusLabel}
    </Badge>
  );
};

type SpendSummaryFooterProps = {
  showOopStatus: boolean;
  oopMaxMet: boolean;
  oopStatusLabel: string;
  oopCostSharingType: string;
  deductibleLimit: number;
  deductibleLimitMet: boolean;
  deductibleStatusLabel: string;
  deductibleCostSharingType: string;
};

const SpendSummaryFooter: React.FC<SpendSummaryFooterProps> = ({
  showOopStatus,
  oopMaxMet,
  oopStatusLabel,
  oopCostSharingType,
  deductibleLimit,
  deductibleLimitMet,
  deductibleStatusLabel,
  deductibleCostSharingType,
}) => {
  return (
    <CardFooter className="absolute bottom-0 mt-4 w-full items-center gap-4 border-t py-2">
      {showOopStatus && (
        <StatusBadge
          isMetCondition={oopMaxMet}
          statusLabel={oopStatusLabel}
          costSharingType={oopCostSharingType}
        />
      )}
      {deductibleLimit > 0 && (
        <StatusBadge
          isMetCondition={deductibleLimitMet}
          statusLabel={deductibleStatusLabel}
          costSharingType={deductibleCostSharingType}
        />
      )}
    </CardFooter>
  );
};

const SpendSummaryItem = ({
  summary,
  planType,
  showFamilyDentalOop = false,
}: {
  summary: any;
  planType: string;
  showFamilyDentalOop?: boolean;
}) => {
  // const UnlimitedLimit = 9000000000;
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );

  const membersLookup = queryClient.getQueryData([
    QueryKeys.MEMBERS_LOOKUP,
  ]) as Record<string, Relation>;

  const currentPlanCoverageInfo = membersLookup[
    summary?.memberId
  ]?.planCoverageInfo?.find(
    (coverage: PlanCoverageInfo) =>
      coverage?.claimCoverageCode === activeCoverage?.claimCoverageCode,
  );

  const renderDeductible = useCallback(() => {
    if (summary?.deductibleLimit === 0) {
      return (
        <span>
          <Label className="text-sm font-semibold text-gray-500">
            There is no deductible limit
            <br />
            <Label className="text-xs text-gray-500">
              {`(${summary?.memberId !== "" ? "Deductible Spend:" : "Family Deductible Spend:"} ${formatCurrency(summary?.deductiblePaid)})`}
            </Label>
          </Label>
        </span>
      );
    } else if (summary?.deductibleLimit === SPEND.MAXIMUM) {
      return (
        <span>
          <Label className="text-sm font-semibold text-gray-500">
            Deductible limit not applicable
            <br />
            <Label className="text-xs text-gray-500">
              {`(${summary?.memberId !== "" ? "Deductible Spend:" : "Family Deductible Spend:"} ${formatCurrency(summary?.deductiblePaid)})`}
            </Label>
          </Label>
        </span>
      );
    } else {
      return (
        <ProgressSection
          title="Deductible"
          paid={
            summary?.deductiblePaid > summary?.deductibleLimit
              ? summary?.deductibleLimit
              : summary?.deductiblePaid
          }
          max={summary?.deductibleLimit}
          type="deductible"
        />
      );
    }
  }, [summary?.deductibleLimit, summary?.deductiblePaid, summary?.memberId]);

  const renderOOP = useCallback(() => {
    const { long: oopLongLabel, short: oopShortLabel } = getOopLabels(
      activeCoverage?.claimCoverageCode,
      summary?.OOPStatus?.costSharingType,
    );
    const isDental = lowerCaseComparison(
      activeCoverage?.claimCoverageCode,
      COVERAGE_CODE.DENTAL,
    );
    const isFamily = summary?.memberId === "";

    const oopSpendMessage = isDental
      ? `(${isFamily ? "Family " : ""}Reimbursement Spend: ${formatCurrency(summary.OOPPaid)})`
      : `(${isFamily ? "Family " : ""}${oopShortLabel}: ${formatCurrency(summary.OOPPaid)})`;

    if (summary.OOPMax === 0) {
      return (
        <span>
          <Label className="text-sm font-semibold text-gray-500">
            {isDental
              ? "There is no reimbursement maximum"
              : `There is no ${oopLongLabel} max`}
            <br />
            <Label className="text-xs text-gray-500">{oopSpendMessage}</Label>
          </Label>
        </span>
      );
    } else if (summary.OOPMax === SPEND.MAXIMUM) {
      return (
        <span>
          <Label className="text-sm font-semibold text-gray-500">
            {isDental
              ? "Reimbursement maximum not applicable"
              : `${oopShortLabel} max not applicable`}
            <br />
            <Label className="text-xs text-gray-500">{oopSpendMessage}</Label>
          </Label>
        </span>
      );
    } else {
      return (
        <ProgressSection
          title={oopLongLabel}
          paid={
            summary?.OOPPaid > summary?.OOPMax
              ? summary?.OOPMax
              : summary?.OOPPaid
          }
          max={summary?.OOPMax}
          type="oop"
        />
      );
    }
  }, [
    summary?.OOPMax,
    summary?.OOPPaid,
    summary?.memberId,
    summary?.OOPStatus?.costSharingType,
    activeCoverage?.claimCoverageCode,
  ]);

  if (!summary) {
    return null;
  }

  if (currentPlanCoverageInfo) {
    const isPlanActive = isCoverageActiveOrExtended(currentPlanCoverageInfo);
    if ((planType === "Family" && summary.type !== planType) || !isPlanActive) {
      return null;
    }
  }

  // Hidden by default; config.showFamilyDentalOop opts a plan back in.
  const hideDentalFamilyOop =
    activeCoverage.claimCoverageCode === COVERAGE_CODE.DENTAL &&
    summary?.type === MEMBER_TYPE.FAMILY &&
    !showFamilyDentalOop;

  return summary.type === "Family" ||
    Object.keys(membersLookup).includes(summary.memberId) ? (
    <Card className="relative min-h-[300px] min-w-[250px] overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-lg">
      <SpendSummaryHeader memberId={summary.memberId} type={summary.type} />

      <CardContent className="space-y-2 py-4">
        {renderDeductible()}

        <Separator className="my-4" />
        {hideDentalFamilyOop ? null : renderOOP()}
      </CardContent>

      {summary.OOPMax !== 0 || summary.deductibleLimit !== 0 ? (
        <SpendSummaryFooter
          showOopStatus={!hideDentalFamilyOop}
          oopStatusLabel={summary?.OOPStatus?.label}
          oopCostSharingType={summary?.OOPStatus?.costSharingType}
          deductibleLimit={summary.deductibleLimit}
          oopMaxMet={summary.OOPStatus.hasMet}
          deductibleLimitMet={summary.deductibleStatus.hasMet}
          deductibleStatusLabel={summary?.deductibleStatus?.label}
          deductibleCostSharingType={
            summary?.deductibleStatus?.deductibleLabelType
          }
        />
      ) : null}
    </Card>
  ) : null;
};

export default SpendSummaryItem;
export type { SpendSummary as ISpendSummary };
